import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, map, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  AuthSessionRedirectService,
  isPublicAuthApiPath,
  shouldRedirectOnAuthFailure,
} from '../auth/auth-session-redirect.service';

function isAppApiRequest(url: string): boolean {
  const base = environment.apiBaseUrl.replace(/\/$/, '');
  const path = url.split('?')[0];
  return path.startsWith(base) || path.includes('/api/v1/');
}

/** Cookie session must be sent on verify-mfa (set cookie) and logout (clear cookie). */
function withCredentialsFor(url: string): boolean {
  const p = url.split('?')[0];
  if (p.includes('/auth/admin/verify-mfa') || p.includes('/auth/admin/logout')) {
    return true;
  }
  return !isPublicAuthApiPath(url);
}

/**
 * Sends cookies on API requests; redirects to sign-in when session/token is expired or invalid.
 */
export const credentialsInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isAppApiRequest(req.url)) {
    return next(req);
  }

  const sessionRedirect = inject(AuthSessionRedirectService);
  const withCreds = req.clone({
    withCredentials: withCredentialsFor(req.url),
  });
  const isLoginFlow = isPublicAuthApiPath(req.url);

  if (isLoginFlow) {
    return next(withCreds);
  }

  return next(withCreds).pipe(
    map((event) => {
      if (event instanceof HttpResponse && shouldRedirectOnAuthFailure(req.url, event.body)) {
        sessionRedirect.redirectToLogin();
        throw new HttpErrorResponse({
          error: event.body,
          status: 401,
          statusText: 'Unauthorized',
          url: event.url ?? req.url,
        });
      }
      return event;
    }),
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && shouldRedirectOnAuthFailure(req.url, err)) {
        sessionRedirect.redirectToLogin();
      }
      return throwError(() => err);
    }),
  );
};
